import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Power } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  CheckCircle,
  XCircle,
  Eye,
  Plus,
  Edit,
  Upload,
  X,
  FileText,
  Search,
  Trash2,
  Download,
  Archive,
  RotateCcw
} from 'lucide-react';
import { exportToExcel } from '@/lib/exportExcel';
import { GoogleAddressSearch } from '@/components/GoogleAddressSearch';
import { MultiAdminCategorySelector } from '@/components/MultiAdminCategorySelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Restaurants = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true); // first load only (full-page spinner)
  const [fetching, setFetching] = useState(false); // subsequent page/filter changes (inline)
  const [vegFilter, setVegFilter] = useState('all'); // all | veg | nonveg
  const [statusFilter, setStatusFilter] = useState('all'); // all | PENDING | APPROVED | REJECTED
  const [openFilter, setOpenFilter] = useState('all'); // all | open | closed
  const [sortBy, setSortBy] = useState('newest'); // newest | priority

  // false when the API that answered doesn't know the `open` filter yet
  // (backend not restarted / not deployed) — we then filter the loaded page
  // in the browser so the tabs still behave, and say why counts are partial.
  const [serverOpenFilter, setServerOpenFilter] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // ------------------ PAGINATION ------------------
  const PAGE_SIZE = 50;
  const [showAll, setShowAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkOpenUpdating, setBulkOpenUpdating] = useState(null); // null | 'open' | 'close'
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState({
    all: 0,
    veg: 0,
    nonveg: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    open: 0,
    closed: 0
  });

  // ------------------ DELETED RESTAURANTS ------------------
  const [showDeletedDialog, setShowDeletedDialog] = useState(false);
  const [deletedRestaurants, setDeletedRestaurants] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedSearch, setDeletedSearch] = useState('');
  const [deletedPage, setDeletedPage] = useState(1);
  const [deletedTotal, setDeletedTotal] = useState(0);
  const [deletedTotalPages, setDeletedTotalPages] = useState(1);
  const [restoringId, setRestoringId] = useState(null);

  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRestaurant, setCurrentRestaurant] = useState(null);

  // ------------------ FORM DATA ------------------
  const [formData, setFormData] = useState({
    name: '',
    ownerName: '',
    userEmail: '',
    phone: '',
    altPhone: '',
    zoneId: '',
    address: '',
    googleAddress: '',
    latitude: '',
    longitude: '',
    pincode: '',
    openTime: '',
    closeTime: '',
    openTime2: '',
    closeTime2: '',
    deliveryTime: '',
    cuisines: '',
    adminCategoryIds: [],
    gstNumber: '',
    fssaiNumber: '',
    panNumber: '',
    accountName: '',
    accountNo: '',
    bankName: '',
    bankCode: '',
    isHome: '',
    priority: '',
    commissionType: 'PERCENTAGE',
    commissionValue: '',
    rainFee: '',
    radius: '',
    twoHrRadius: '',
    isRainMode: false,
    isOpen: false,
    isPureVeg: false
  });

  // ------------------ FILES (ACTUAL FILE OBJECTS) ------------------
  const [frontImageFile, setFrontImageFile] = useState(null);
  const [kitchenImageFile, setKitchenImageFile] = useState(null);
  const [chequeFile, setChequeFile] = useState(null);
  const [removeFrontImage, setRemoveFrontImage] = useState(false);

  const [menuFiles, setMenuFiles] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);

  // ------------------ PREVIEWS ------------------
  const [frontPreview, setFrontPreview] = useState('');
  const [kitchenPreview, setKitchenPreview] = useState('');
  const [menuPreviews, setMenuPreviews] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);

  // existing S3 urls (edit mode)
  const [existingMenuUrls, setExistingMenuUrls] = useState([]);
  const [existingGalleryUrls, setExistingGalleryUrls] = useState([]);
  const [existingChequeUrl, setExistingChequeUrl] = useState('');

  useEffect(() => {
    fetchZones();
  }, []);

  // Debounce the search box so we're not hitting the server on every
  // keystroke — reset to page 1 whenever the (debounced) query changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [searchQuery]);

  // Re-fetch whenever page, search, the veg/non-veg tab, status filter, or
  // Show All changes.
  useEffect(() => {
    fetchRestaurants();
    // eslint-disable-next-line
  }, [page, debouncedQuery, vegFilter, statusFilter, openFilter, sortBy, showAll]);

  // cleanup object urls
  useEffect(() => {
    return () => {
      if (frontPreview?.startsWith('blob:')) URL.revokeObjectURL(frontPreview);
      if (kitchenPreview?.startsWith('blob:')) URL.revokeObjectURL(kitchenPreview);

      menuPreviews.forEach((p) => p.startsWith('blob:') && URL.revokeObjectURL(p));
      galleryPreviews.forEach((p) => p.startsWith('blob:') && URL.revokeObjectURL(p));
    };
    // eslint-disable-next-line
  }, []);

  const fetchRestaurants = async () => {
    try {
      setFetching(true);
      const response = await axios.get(`${API}/restaurants`, {
        params: {
          page: showAll ? 1 : page,
          // "Show All" bypasses pagination entirely rather than guessing a
          // large-enough limit.
          limit: showAll ? 100000 : PAGE_SIZE,
          q: debouncedQuery || undefined,
          veg: vegFilter,
          status: statusFilter,
          open: openFilter,
          sort: sortBy
        }
      });
      const { data = [], pagination = {}, counts: c = {} } = response.data || {};
      const isOpenRow = (r) => r?.isOpen === true || r?.isOpen === 'true';
      const supported = c.open !== undefined && c.closed !== undefined;
      setServerOpenFilter(supported);
      const rows = supported || openFilter === 'all'
        ? data
        : data.filter((r) => (openFilter === 'open' ? isOpenRow(r) : !isOpenRow(r)));
      setRestaurants(rows);
      setTotal(pagination.total || 0);
      setTotalPages(pagination.totalPages || 1);
      setCounts({
        all: c.all || 0,
        veg: c.veg || 0,
        nonveg: c.nonveg || 0,
        pending: c.pending || 0,
        approved: c.approved || 0,
        rejected: c.rejected || 0,
        open: supported ? c.open || 0 : data.filter(isOpenRow).length,
        closed: supported ? c.closed || 0 : data.filter((r) => !isOpenRow(r)).length
      });
      setSelectedIds([]);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
      toast.error('Failed to fetch restaurants');
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === restaurants.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(restaurants.map((r) => r.id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(
        `Delete ${selectedIds.length} selected restaurant(s)? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      setBulkDeleting(true);
      await axios.post(`${API}/restaurants/bulk-delete`, { ids: selectedIds });
      toast.success(`${selectedIds.length} restaurant(s) deleted`);
      setSelectedIds([]);
      fetchRestaurants();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Failed to delete selected restaurants');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Bulk open / close the selected restaurants (manual isOpen switch)
  const handleBulkOpenStatus = async (open) => {
    if (selectedIds.length === 0 || bulkOpenUpdating) return;
    const word = open ? 'open' : 'close';
    if (!window.confirm(`Mark ${selectedIds.length} selected restaurant(s) as ${open ? 'OPEN' : 'CLOSED'}?`)) return;
    try {
      setBulkOpenUpdating(open ? 'open' : 'close');
      const res = await axios.patch(`${API}/restaurants/bulk/open-status`, {
        ids: selectedIds,
        isOpen: open
      });
      const { modified = 0, matched = 0 } = res.data || {};
      toast.success(
        modified === matched
          ? `${modified} restaurant(s) ${open ? 'opened' : 'closed'}`
          : `${modified} updated, ${matched - modified} already ${open ? 'open' : 'closed'}`
      );
      setSelectedIds([]);
      fetchRestaurants();
    } catch (error) {
      console.error(`Bulk ${word} failed:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${word} selected restaurants`);
    } finally {
      setBulkOpenUpdating(null);
    }
  };

  // ------------------ DELETED RESTAURANTS ------------------
  const fetchDeletedRestaurants = async (targetPage = deletedPage, query = deletedSearch) => {
    try {
      setDeletedLoading(true);
      const response = await axios.get(`${API}/restaurants/deleted`, {
        params: { page: targetPage, limit: PAGE_SIZE, q: query || undefined }
      });
      const { data = [], pagination = {} } = response.data || {};
      setDeletedRestaurants(data);
      setDeletedTotal(pagination.total || 0);
      setDeletedTotalPages(pagination.totalPages || 1);
    } catch (error) {
      console.error('Failed to fetch deleted restaurants:', error);
      toast.error('Failed to fetch deleted restaurants');
    } finally {
      setDeletedLoading(false);
    }
  };

  const openDeletedDialog = () => {
    setShowDeletedDialog(true);
    setDeletedPage(1);
    fetchDeletedRestaurants(1, deletedSearch);
  };

  // Re-fetch whenever the deleted dialog's page or (debounced) search changes.
  useEffect(() => {
    if (!showDeletedDialog) return;
    const t = setTimeout(() => {
      fetchDeletedRestaurants(deletedPage, deletedSearch);
    }, deletedSearch ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [showDeletedDialog, deletedPage, deletedSearch]);

  const handleRestore = async (restaurant) => {
    if (!window.confirm(`Restore "${restaurant.name}"? It will reappear in the active restaurants list.`)) {
      return;
    }
    try {
      setRestoringId(restaurant.id);
      await axios.post(`${API}/restaurants/${restaurant.id}/restore`);
      toast.success(`"${restaurant.name}" restored`);
      fetchDeletedRestaurants();
      fetchRestaurants();
    } catch (error) {
      console.error('Restore failed:', error);
      toast.error(error?.response?.data?.detail || 'Failed to restore restaurant');
    } finally {
      setRestoringId(null);
    }
  };

  const handleClearPaymentReminder = async (restaurant) => {
    if (
      !window.confirm(
        `Mark the pending settlement for "${restaurant.name}" as paid? This clears the Payment Due flag until the next weekly check.`
      )
    ) {
      return;
    }
    try {
      await axios.post(`${API}/restaurants/${restaurant.id}/clear-payment-reminder`);
      toast.success('Payment reminder cleared');
      fetchRestaurants();
    } catch (error) {
      console.error('Failed to clear payment reminder:', error);
      toast.error('Failed to clear payment reminder');
    }
  };

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API}/zones`);
      const list = Array.isArray(response.data) ? response.data : [];
      setZones(list.filter((z) => z && !z.isDeleted));
    } catch (error) {
      console.error('Failed to fetch zones:', error);
    }
  };

  const resetAll = () => {
    setFormData({
      name: '',
      ownerName: '',
      userEmail: '',
      phone: '',
      altPhone: '',
      zoneId: '',
      address: '',
      googleAddress: '',
      latitude: '',
      longitude: '',
      pincode: '',
      openTime: '',
      closeTime: '',
      openTime2: '',
      closeTime2: '',
      deliveryTime: '',
      cuisines: '',
      adminCategoryIds: [],
      gstNumber: '',
      fssaiNumber: '',
      panNumber: '',
      accountName: '',
      accountNo: '',
      isHome: '',
      priority: '',
      bankName: '',
      bankCode: '',
      commissionType: 'PERCENTAGE',
      commissionValue: '',
      rainFee: '',
      radius: '',
      twoHrRadius: '',
      isRainMode: false,
      isOpen: false,
      isPureVeg: false
    });

    setFrontImageFile(null);
    setKitchenImageFile(null);
    setChequeFile(null);
    setMenuFiles([]);
    setGalleryFiles([]);

    setFrontPreview('');
    setKitchenPreview('');
    setMenuPreviews([]);
    setGalleryPreviews([]);

    setExistingMenuUrls([]);
    setExistingGalleryUrls([]);
    setExistingChequeUrl('');
  };

  const openCreateDialog = () => {
    setEditMode(false);
    setCurrentRestaurant(null);
    resetAll();
    setShowDialog(true);
  };

  const openEditDialog = (restaurant) => {
    setEditMode(true);
    setCurrentRestaurant(restaurant);

    resetAll();

    setFormData({
      name: restaurant.name || '',
      ownerName: restaurant.ownerName || '',
      userEmail: restaurant.userEmail || '',
      phone: restaurant.phone || '',
      altPhone: restaurant.altPhone || '',
      zoneId: restaurant.zoneId ? String(restaurant.zoneId) : '',
      address: restaurant.address || '',
      googleAddress: restaurant.googleAddress || '',
      latitude: restaurant.latitude || '',
      longitude: restaurant.longitude || '',
      pincode: restaurant.pincode || '',
      openTime: restaurant.openTime || '',
      closeTime: restaurant.closeTime || '',
      openTime2: restaurant.openTime2 || '',
      closeTime2: restaurant.closeTime2 || '',
      deliveryTime: restaurant.deliveryTime || '',
      cuisines: Array.isArray(restaurant.cuisines) ? restaurant.cuisines.join(', ') : '',
      adminCategoryIds: Array.isArray(restaurant.adminCategoryIds)
        ? restaurant.adminCategoryIds.map((c) =>
            typeof c === 'object' && c !== null ? String(c._id || c.id || c) : String(c)
          )
        : [],
      gstNumber: restaurant.gstNumber || '',
      fssaiNumber: restaurant.fssaiNumber || '',
      panNumber: restaurant.panNumber || '',
      accountName: restaurant.accountName || '',
      accountNo: restaurant.accountNo || '',
      bankName: restaurant.bankName || '',
      bankCode: restaurant.bankCode || '',
      commissionType: restaurant.commissionType || 'PERCENTAGE',
      commissionValue: restaurant.commissionValue || '',
      rainFee: restaurant.rainFee || '',
      radius: restaurant.radius || '',
      twoHrRadius: restaurant.twoHrRadius || '',
      isHome: restaurant.isHome || '',
      priority: Number(restaurant.priority) > 0 ? String(restaurant.priority) : '',
      isRainMode: restaurant.isRainMode || false,
      isOpen: restaurant.isOpen || false,
      isPureVeg: restaurant.isPureVeg || false
    });

    // existing S3 urls
    setFrontPreview(restaurant.frontImage || '');
    setKitchenPreview(restaurant.kitchenImage || '');
    setFrontImageFile(null);
    setRemoveFrontImage(false);
    setExistingChequeUrl(restaurant.cancelledCheque || '');
    setExistingMenuUrls(Array.isArray(restaurant.menuImages) ? restaurant.menuImages : []);
    setExistingGalleryUrls(Array.isArray(restaurant.galleryImages) ? restaurant.galleryImages : []);

    setShowDialog(true);
  };

  // ------------------ FILE HANDLERS ------------------

  const handleFrontImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFrontImageFile(file);
    setFrontPreview(URL.createObjectURL(file));
    setRemoveFrontImage(false);
  };

  const handleRemoveFrontImage = () => {
    setFrontImageFile(null);
    setFrontPreview('');
    setRemoveFrontImage(true);
  };

  const handleKitchenImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setKitchenImageFile(file);
    setKitchenPreview(URL.createObjectURL(file));
  };

  // cheque: ONLY PDF
 const handleChequeChange = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const allowedTypes = [
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png"
  ];

  if (!allowedTypes.includes(file.type)) {
    toast.error("Cancelled cheque must be PDF / JPG / PNG only");
    e.target.value = "";
    return;
  }

  setChequeFile(file);
  toast.success("Cheque file selected");
};


  // menu: multiple images  
  const handleMenuFilesChange = (e) => {
  const files = Array.from(e.target.files || []);
  if (!files.length) return;

  const onlyImages = files.filter((f) => f.type.startsWith('image/'));

  if (onlyImages.length !== files.length) {
    toast.error('Menu files must be images only');
  }

  // ✅ APPEND files
  setMenuFiles((prev) => [...prev, ...onlyImages]);

  // ✅ APPEND previews
  const newPreviews = onlyImages.map((f) => URL.createObjectURL(f));
  setMenuPreviews((prev) => [...prev, ...newPreviews]);

  // 🔥 Important: reset input value so user can re-select same file again
  e.target.value = '';
};


  // gallery: multiple images
const handleGalleryFilesChange = (e) => {
  const files = Array.from(e.target.files || []);
  if (!files.length) return;

  const onlyImages = files.filter((f) => f.type.startsWith('image/'));

  if (onlyImages.length !== files.length) {
    toast.error('Gallery files must be images only');
  }

  setGalleryFiles((prev) => [...prev, ...onlyImages]);

  const newPreviews = onlyImages.map((f) => URL.createObjectURL(f));
  setGalleryPreviews((prev) => [...prev, ...newPreviews]);

  e.target.value = '';
};


  // ------------------ SUBMIT ------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // IMPORTANT: Use FormData for file upload
      const payload = new FormData();

      // normal fields
      payload.append('name', formData.name);
      payload.append('ownerName', formData.ownerName);
      payload.append('userEmail', formData.userEmail);
      payload.append('phone', formData.phone);
      payload.append('altPhone', formData.altPhone);
      payload.append('zoneId', formData.zoneId || '');
      payload.append('address', formData.address);
      payload.append('googleAddress', formData.googleAddress);
      payload.append('latitude', formData.latitude);
      payload.append('longitude', formData.longitude);
      payload.append('pincode', formData.pincode);
      payload.append('openTime', formData.openTime);
      payload.append('closeTime', formData.closeTime);
      payload.append('openTime2', formData.openTime2);
      payload.append('closeTime2', formData.closeTime2);
      payload.append('deliveryTime', formData.deliveryTime);
      payload.append('cuisines', formData.cuisines); // backend will parse
      payload.append(
        'adminCategoryIds',
        JSON.stringify(formData.adminCategoryIds || [])
      );
      payload.append('gstNumber', formData.gstNumber);
      payload.append('fssaiNumber', formData.fssaiNumber);
      payload.append('panNumber', formData.panNumber);

      payload.append('accountName', formData.accountName);
      payload.append('accountNo', formData.accountNo);
      payload.append('bankName', formData.bankName);
      payload.append('bankCode', formData.bankCode);

      payload.append('commissionType', formData.commissionType);
      payload.append('commissionValue', formData.commissionValue);

      payload.append('radius', formData.radius);
      payload.append('twoHrRadius', formData.twoHrRadius);

      payload.append('isPureVeg', formData.isPureVeg);
      payload.append('isHome', formData.isHome);
      payload.append('priority', formData.priority === '' ? '0' : String(formData.priority));

      // files
      if (frontImageFile) payload.append('frontImage', frontImageFile);
      if (kitchenImageFile) payload.append('kitchenImage', kitchenImageFile);
      if (removeFrontImage && !frontImageFile) payload.append('removeFrontImage', 'true');

      // cheque pdf
      if (chequeFile) payload.append('cancelledCheque', chequeFile);

      // multiple
      menuFiles.forEach((f) => payload.append('menuFiles', f));
      galleryFiles.forEach((f) => payload.append('galleryFiles', f));

      if (editMode && currentRestaurant) {
        await axios.patch(`${API}/restaurants/${currentRestaurant.id}`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Restaurant updated successfully');
      } else {
        await axios.post(`${API}/restaurants`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Restaurant created successfully');
      }

      setShowDialog(false);
      fetchRestaurants();
    } catch (error) {
      console.error('Failed to save restaurant:', error);
      toast.error(error.response?.data?.detail || 'Failed to save restaurant');
    }
  };

  const updateRestaurantStatus = async (id, updates) => {
    try {
      await axios.patch(`${API}/restaurants/${id}`, updates);
      toast.success('Restaurant updated successfully');
      fetchRestaurants();
    } catch (error) {
      console.error('Failed to update restaurant:', error);
      toast.error('Failed to update restaurant');
    }
  };

  const toggleRestaurantOpen = async (restaurant) => {
    const next = !restaurant.isOpen;
    // optimistic update
    setRestaurants((prev) =>
      prev.map((r) => (r.id === restaurant.id ? { ...r, isOpen: next } : r))
    );
    try {
      await axios.patch(`${API}/restaurants/${restaurant.id}`, { isOpen: next });
      toast.success(next ? 'Restaurant marked Open' : 'Restaurant marked Closed');
      // Keep the Open / Closed counts right; with an Open/Closed filter on,
      // reload so the row leaves the list it no longer belongs to.
      setCounts((c) => ({
        ...c,
        open: Math.max(0, (c.open || 0) + (next ? 1 : -1)),
        closed: Math.max(0, (c.closed || 0) + (next ? -1 : 1))
      }));
      if (openFilter !== 'all') fetchRestaurants();
    } catch (error) {
      // rollback
      setRestaurants((prev) =>
        prev.map((r) =>
          r.id === restaurant.id ? { ...r, isOpen: !next } : r
        )
      );
      console.error('Failed to toggle isOpen:', error);
      toast.error('Failed to update open status');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      APPROVED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={`${variants[status] || variants.PENDING} border-0`}>
        {status || 'PENDING'}
      </Badge>
    );
  };

  // ------------------ PREVIEW GRID COMPONENT ------------------
  const PreviewGrid = ({ urls = [] }) => {
    if (!urls.length) return null;

    return (
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {urls.map((u, idx) => (
          <div key={idx} className="relative rounded-lg border overflow-hidden">
            <img src={u} alt="" className="h-20 w-full object-cover" />
          </div>
        ))}
      </div>
    );
  };

  // ------------------ LOADING ------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // ------------------ UI ------------------
  // Filtering (search + veg/non-veg) now happens server-side so it applies
  // across the whole dataset, not just whatever page is currently loaded.

  return (
    <div className="p-4 sm:p-6 lg:p-8" style={{ fontFamily: 'Public Sans, sans-serif' }}>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0F172A' }}>
            Restaurants
          </h1>
          <p className="text-gray-600">Manage and verify restaurant applications</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          data-testid="export-restaurants-button"
          onClick={() => {
            const rows = restaurants.map((r) => ({
              Name: r.name || '',
              Owner: r.ownerName || '',
              Phone: r.phone || '',
              Address: r.address || '',
              Priority: Number(r.priority) > 0 ? Number(r.priority) : '',
              Status: r.status ? 'Active' : 'Inactive',
              Open: r.isOpen ? 'Open' : 'Closed',
              Verification: r.verificationStatus || 'PENDING',
              Registered: r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '',
            }));
            if (!exportToExcel(rows, 'restaurants')) {
              toast.error('No restaurants to export');
            }
          }}
        >
          <Download size={16} className="mr-2" />
          Export
        </Button>
        <Button
          variant="outline"
          data-testid="deleted-restaurants-button"
          onClick={openDeletedDialog}
        >
          <Archive size={16} className="mr-2" />
          Deleted Restaurants
        </Button>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button
              onClick={openCreateDialog}
              data-testid="create-restaurant-button"
              className="bg-primary text-white"
              style={{ backgroundColor: '#C2410C' }}
            >
              <Plus size={20} className="mr-2" />
              Add Restaurant
            </Button>
          </DialogTrigger>

          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => {
            // ✅ Prevent closing when selecting Google autocomplete suggestion
            if (e.target?.closest(".pac-container")) {
              e.preventDefault();
            }
          }}
          >
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Restaurant' : 'Create New Restaurant'}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit}>
              <Tabs defaultValue="basic" className="w-full">
                {/* Below sm: falls back to the TabsList default (inline-flex,
                    nowrap triggers) inside a scroll container, so tab labels
                    stay readable instead of being squeezed into 5 equal
                    columns. At sm+ it's the original full-width 5-col grid. */}
                <div className="overflow-x-auto">
                <TabsList className="sm:grid sm:w-full sm:grid-cols-5">
                  <TabsTrigger value="basic">Basic</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="images">Images</TabsTrigger>
                  <TabsTrigger value="bank">Bank</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>
                </div>

                {/* ---------------- BASIC ---------------- */}
                <TabsContent value="basic" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Restaurant Name *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Owner Name</Label>
                      <Input
                        value={formData.ownerName}
                        onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.userEmail}
                        onChange={(e) => setFormData({ ...formData, userEmail: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        data-testid="restaurant-phone-input"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Alternate Phone</Label>
                      <Input
                        data-testid="restaurant-alt-phone-input"
                        placeholder="Optional secondary contact"
                        value={formData.altPhone}
                        onChange={(e) => setFormData({ ...formData, altPhone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Zone</Label>
                      <Select
                        value={formData.zoneId || 'none'}
                        onValueChange={(val) =>
                          setFormData({ ...formData, zoneId: val === 'none' ? '' : val })
                        }
                      >
                        <SelectTrigger data-testid="restaurant-zone-select">
                          <SelectValue placeholder="Select a zone" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— No zone —</SelectItem>
                          {zones.map((z) => (
                            <SelectItem key={z.id} value={z.id}>
                              {z.zoneName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Address</Label>
                    <Textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={2}
                    />
                  </div>
               <GoogleAddressSearch
                  label="Google Address"
                  value={formData.googleAddress}
                  onChangeValue={(val) =>
                    setFormData((prev) => ({ ...prev, googleAddress: val }))
                  }
                  onSelectPlace={(data) => {
                    setFormData((prev) => ({
                      ...prev,
                      googleAddress: data.googleAddress,
                      latitude: data.latitude,
                      longitude: data.longitude,
                      pincode: data.pincode
                    }));
                  }}
                />



                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label>Latitude</Label>
                      <Input
                        type="number"
                        step="any"
                        value={formData.latitude}
                        onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Longitude</Label>
                      <Input
                        type="number"
                        step="any"
                        value={formData.longitude}
                        onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Pincode</Label>
                      <Input
                        value={formData.pincode}
                        onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Open Time (Slot 1)</Label>
                      <Input
                        type="time"
                        value={formData.openTime}
                        onChange={(e) => setFormData({ ...formData, openTime: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Close Time (Slot 1)</Label>
                      <Input
                        type="time"
                        value={formData.closeTime}
                        onChange={(e) => setFormData({ ...formData, closeTime: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Open Time (Slot 2 — optional)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="time"
                          value={formData.openTime2}
                          onChange={(e) => setFormData({ ...formData, openTime2: e.target.value })}
                        />
                        {formData.openTime2 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            title="Clear open time (Slot 2)"
                            onClick={() => setFormData({ ...formData, openTime2: '' })}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label>Close Time (Slot 2 — optional)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="time"
                          value={formData.closeTime2}
                          onChange={(e) => setFormData({ ...formData, closeTime2: e.target.value })}
                        />
                        {formData.closeTime2 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            title="Clear close time (Slot 2)"
                            onClick={() => setFormData({ ...formData, closeTime2: '' })}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 -mt-2">
                    E.g. lunch 11:00–15:00 and dinner 18:00–23:00. Leave Slot 2 blank for a single continuous window.
                  </p>

                  <div>
                    <Label>Cuisines (comma separated)</Label>
                    <Input
                      placeholder="e.g., Italian, Chinese"
                      value={formData.cuisines}
                      onChange={(e) => setFormData({ ...formData, cuisines: e.target.value })}
                    />
                  </div>

                  <MultiAdminCategorySelector
                    label="Admin Categories"
                    value={formData.adminCategoryIds}
                    onChange={(ids) =>
                      setFormData({ ...formData, adminCategoryIds: ids })
                    }
                  />

                  <div>
                    <Label>Pure Veg</Label>
                    <div className="flex gap-2 mt-2" data-testid="restaurant-pureveg-toggle">
                      <button
                        type="button"
                        data-testid="restaurant-pureveg-yes"
                        onClick={() => setFormData({ ...formData, isPureVeg: true })}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          formData.isPureVeg
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        data-testid="restaurant-pureveg-no"
                        onClick={() => setFormData({ ...formData, isPureVeg: false })}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          !formData.isPureVeg
                            ? 'bg-gray-800 text-white border-gray-800'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                        }`}
                      >
                        No
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Mark this restaurant as serving only vegetarian items.
                    </p>
                  </div>
                </TabsContent>

                {/* ---------------- DOCUMENTS ---------------- */}
                <TabsContent value="documents" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>GST Number</Label>
                      <Input
                        value={formData.gstNumber}
                        onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>FSSAI Number</Label>
                      <Input
                        value={formData.fssaiNumber}
                        onChange={(e) => setFormData({ ...formData, fssaiNumber: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>PAN Number</Label>
                    <Input
                      value={formData.panNumber}
                      onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })}
                    />
                  </div>

                  {/* Cheque PDF */}
                  <div>
                    <Label>Cancelled Cheque</Label>

                    <div className="flex items-center gap-3 mt-2">
                      <Input
                        type="file"
                        accept="application/pdf,image/*"
                        onChange={handleChequeChange}
                      />

                    </div>

                    {/* existing cheque */}
                    {(existingChequeUrl || chequeFile) && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                        <FileText size={16} />
                        {chequeFile ? (
                          <span className="font-medium">{chequeFile.name}</span>
                        ) : (
                          <a
                            href={existingChequeUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 underline"
                          >
                            View uploaded PDF
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* ---------------- IMAGES ---------------- */}
                <TabsContent value="images" className="space-y-6 mt-4">
                  {/* Front */}
                  <div>
                    <Label>Front Image (Upload)</Label>
                    <Input type="file" accept="image/*" onChange={handleFrontImageChange} />

                    {frontPreview ? (
                      <div className="mt-3 flex items-start gap-2">
                        <img
                          src={frontPreview}
                          alt="Front Preview"
                          className="h-28 w-44 object-cover rounded-xl border"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={handleRemoveFrontImage}
                        >
                          <Trash2 size={14} className="mr-1" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      removeFrontImage && (
                        <p className="mt-2 text-xs text-gray-500">
                          Front image will be removed when saved.
                        </p>
                      )
                    )}
                  </div>

                  {/* Kitchen */}
                  <div>
                    <Label>Kitchen Image (Upload)</Label>
                    <Input type="file" accept="image/*" onChange={handleKitchenImageChange} />

                    {kitchenPreview && (
                      <img
                        src={kitchenPreview}
                        alt="Kitchen Preview"
                        className="mt-3 h-28 w-44 object-cover rounded-xl border"
                      />
                    )}
                  </div>

                  {/* Menu multiple */}
                  <div>
                    <Label>Menu Images (Multiple Upload)</Label>
                    <Input type="file" accept="image/*" multiple onChange={handleMenuFilesChange} />

                    {/* existing menu urls */}
                    {existingMenuUrls.length > 0 && (
                      <>
                        <p className="text-xs text-gray-500 mt-2">Existing Menu Images</p>
                        <PreviewGrid urls={existingMenuUrls} />
                      </>
                    )}

                    {/* new menu previews */}
                    {menuPreviews.length > 0 && (
                      <>
                        <p className="text-xs text-gray-500 mt-2">New Selected Menu Images</p>
                        <PreviewGrid urls={menuPreviews} />
                      </>
                    )}
                  </div>

                  {/* Gallery multiple */}
                  <div>
                    <Label>Gallery Images (Multiple Upload)</Label>
                    <Input type="file" accept="image/*" multiple onChange={handleGalleryFilesChange} />

                    {/* existing gallery urls */}
                    {existingGalleryUrls.length > 0 && (
                      <>
                        <p className="text-xs text-gray-500 mt-2">Existing Gallery Images</p>
                        <PreviewGrid urls={existingGalleryUrls} />
                      </>
                    )}

                    {/* new gallery previews */}
                    {galleryPreviews.length > 0 && (
                      <>
                        <p className="text-xs text-gray-500 mt-2">New Selected Gallery Images</p>
                        <PreviewGrid urls={galleryPreviews} />
                      </>
                    )}
                  </div>
                </TabsContent>

                {/* ---------------- BANK ---------------- */}
                <TabsContent value="bank" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Account Holder Name</Label>
                      <Input
                        value={formData.accountName}
                        onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Account Number</Label>
                      <Input
                        value={formData.accountNo}
                        onChange={(e) => setFormData({ ...formData, accountNo: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Bank Name</Label>
                      <Input
                        value={formData.bankName}
                        onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>IFSC / Bank Code</Label>
                      <Input
                        value={formData.bankCode}
                        onChange={(e) => setFormData({ ...formData, bankCode: e.target.value })}
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* ---------------- SETTINGS ---------------- */}
                <TabsContent value="settings" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Is Home</Label>
                      <Select
                          value={formData.isHome}
                          onValueChange={(value) =>
                              setFormData({ ...formData, isHome: value })
                          }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="restaurant-priority">
                        Priority{' '}
                        <span className="text-xs font-normal text-gray-500">(1 = top, leave blank for none)</span>
                      </Label>
                      <Input
                        id="restaurant-priority"
                        data-testid="restaurant-priority-input"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={9999}
                        step={1}
                        placeholder="e.g. 1"
                        value={formData.priority}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                          setFormData((prev) => ({ ...prev, priority: digits }));
                        }}
                      />
                    </div>

                    <div>
                      <Label>Commission Type <span className="text-xs font-normal text-gray-500">(default — category rules in Commission Management override it)</span></Label>
                      <Select
                        value={formData.commissionType}
                        onValueChange={(value) =>
                          setFormData({ ...formData, commissionType: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                          <SelectItem value="FLAT">Flat Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Commission Value</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.commissionValue}
                        onChange={(e) => setFormData({ ...formData, commissionValue: e.target.value })}
                      />
                    </div>
                  </div>

                  

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Delivery Radius (km)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.radius}
                        onChange={(e) => setFormData({ ...formData, radius: e.target.value })}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="mt-6 flex gap-2">
                <Button type="submit" className="flex-1" style={{ backgroundColor: '#C2410C' }}>
                  {editMode ? 'Update Restaurant' : 'Create Restaurant'}
                </Button>

                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* ---------------- DELETED RESTAURANTS ---------------- */}
      <Dialog open={showDeletedDialog} onOpenChange={setShowDeletedDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Deleted Restaurants</DialogTitle>
          </DialogHeader>

          <div className="relative mt-2 mb-4">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <Input
              type="text"
              placeholder="Search by name or phone..."
              value={deletedSearch}
              onChange={(e) => {
                setDeletedSearch(e.target.value);
                setDeletedPage(1);
              }}
              className="pl-9"
              data-testid="deleted-restaurants-search"
            />
          </div>

          {deletedLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : deletedRestaurants.length === 0 ? (
            <p className="text-center text-gray-500 py-12">No deleted restaurants found.</p>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <th className="px-4 py-3">Restaurant</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Deleted On</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {deletedRestaurants.map((restaurant) => (
                    <tr key={restaurant.id} data-testid={`deleted-restaurant-row-${restaurant.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{restaurant.name}</div>
                        <div className="text-xs text-gray-500">{restaurant.ownerName || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{restaurant.phone || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{restaurant.userEmail || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {restaurant.updatedAt
                          ? new Date(restaurant.updatedAt).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={restoringId === restaurant.id}
                          data-testid={`restore-restaurant-${restaurant.id}`}
                          onClick={() => handleRestore(restaurant)}
                        >
                          <RotateCcw size={14} className="mr-1" />
                          {restoringId === restaurant.id ? 'Restoring...' : 'Restore'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {deletedTotalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-gray-500">
                Page {deletedPage} of {deletedTotalPages} ({deletedTotal} total)
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={deletedPage <= 1}
                  onClick={() => setDeletedPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={deletedPage >= deletedTotalPages}
                  onClick={() => setDeletedPage((p) => Math.min(deletedTotalPages, p + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------------- SEARCH + VEG FILTER ---------------- */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            type="text"
            placeholder="Search by name or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
            data-testid="restaurants-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              data-testid="restaurants-search-clear"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div
          className="flex flex-wrap gap-2"
          data-testid="restaurants-veg-filter-bar"
        >
        {[
          { v: 'all', label: 'All', count: counts.all },
          { v: 'veg', label: 'Pure Veg', count: counts.veg },
          { v: 'nonveg', label: 'Non-Veg', count: counts.nonveg }
        ].map(({ v, label, count }) => {
          const active = vegFilter === v;
          const dot =
            v === 'veg'
              ? 'bg-green-600'
              : v === 'nonveg'
              ? 'bg-red-600'
              : null;
          return (
            <button
              key={v}
              type="button"
              data-testid={`veg-chip-${v}`}
              onClick={() => {
                setVegFilter(v);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors flex items-center gap-1.5 ${
                active
                  ? v === 'veg'
                    ? 'bg-green-600 text-white border-green-600'
                    : v === 'nonveg'
                    ? 'bg-gray-800 text-white border-gray-800'
                    : 'bg-orange-600 text-white border-orange-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
              }`}
            >
              {dot && (
                <span
                  className={`w-2 h-2 rounded-full ${
                    active ? 'bg-white' : dot
                  }`}
                />
              )}
              {label}
              <span className={active ? 'opacity-90' : 'text-gray-500'}>
                ({count})
              </span>
            </button>
          );
        })}
        </div>

        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger
            className="w-full sm:w-[180px]"
            data-testid="restaurants-status-filter"
          >
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses ({counts.all})</SelectItem>
            <SelectItem value="PENDING">Pending ({counts.pending})</SelectItem>
            <SelectItem value="APPROVED">Approved ({counts.approved})</SelectItem>
            <SelectItem value="REJECTED">Rejected ({counts.rejected})</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={sortBy}
          onValueChange={(v) => {
            setSortBy(v);
            setPage(1);
          }}
        >
          <SelectTrigger
            className="w-full sm:w-[170px]"
            data-testid="restaurants-sort"
            aria-label="Sort restaurants"
          >
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Sort: Newest first</SelectItem>
            <SelectItem value="priority">Sort: Priority</SelectItem>
          </SelectContent>
        </Select>

        {/* Open / Closed / All */}
        <div
          className="inline-flex shrink-0 rounded-lg border bg-gray-50 p-1"
          role="tablist"
          aria-label="Filter by open status"
          data-testid="restaurants-open-filter"
        >
          {[
            { v: 'all', label: 'All', count: counts.all },
            { v: 'open', label: 'Open', count: counts.open, dot: 'bg-green-500' },
            { v: 'closed', label: 'Closed', count: counts.closed, dot: 'bg-gray-400' }
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              role="tab"
              aria-selected={openFilter === o.v}
              data-testid={`restaurants-open-filter-${o.v}`}
              onClick={() => {
                setOpenFilter(o.v);
                setPage(1);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                openFilter === o.v
                  ? 'bg-white shadow-sm font-medium text-gray-900'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {o.dot && <span className={`h-2 w-2 rounded-full ${o.dot}`} />}
              {o.label}
              <span className="text-xs text-gray-400">({o.count})</span>
            </button>
          ))}
        </div>
        {!serverOpenFilter && (
          <span
            className="text-xs text-amber-700"
            title="The admin API that answered doesn't support the Open/Closed filter yet. Restart (or deploy) the admin backend to filter across all pages."
          >
            Filtering this page only — update the admin backend for full results
          </span>
        )}
      </div>

      {/* ---------------- SHOW ALL / BULK DELETE BAR ---------------- */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="restaurants-show-all-toggle"
          onClick={() => {
            setShowAll((v) => !v);
            setPage(1);
          }}
        >
          {showAll ? 'Show Paginated' : 'Show All'}
        </Button>

        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {selectedIds.length} selected
            </span>
            {can('restaurants', 'edit') && (
              <>
                <Button
                  type="button"
                  size="sm"
                  disabled={!!bulkOpenUpdating}
                  data-testid="restaurants-open-selected"
                  onClick={() => handleBulkOpenStatus(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Power size={14} className="mr-1" />
                  {bulkOpenUpdating === 'open' ? 'Opening...' : 'Open Selected'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!!bulkOpenUpdating}
                  data-testid="restaurants-close-selected"
                  onClick={() => handleBulkOpenStatus(false)}
                >
                  <Power size={14} className="mr-1" />
                  {bulkOpenUpdating === 'close' ? 'Closing...' : 'Close Selected'}
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={bulkDeleting}
              data-testid="restaurants-delete-selected"
              onClick={handleBulkDelete}
            >
              <Trash2 size={14} className="mr-1" />
              {bulkDeleting ? 'Deleting...' : 'Delete Selected'}
            </Button>
          </div>
        )}
      </div>

      {/* ---------------- TABLE ---------------- */}
      <div
        className={`bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mt-8 transition-opacity ${
          fetching ? 'opacity-60 pointer-events-none' : ''
        }`}
      >
        <div className="max-h-[72vh] overflow-auto">
          <table className="w-full min-w-[1200px] table-fixed border-collapse">
            <thead className="sticky top-0 z-10 bg-white border-b border-gray-200">
              <tr className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <th className="px-4 py-4 w-[40px]">
                  <input
                    type="checkbox"
                    data-testid="restaurants-select-all"
                    checked={
                      restaurants.length > 0 &&
                      selectedIds.length === restaurants.length
                    }
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="px-6 py-4 w-[320px]">Restaurant</th>
                <th className="px-6 py-4 w-[110px]">
                  <button
                    type="button"
                    data-testid="restaurants-priority-sort"
                    onClick={() => {
                      setSortBy((prev) => (prev === 'priority' ? 'newest' : 'priority'));
                      setPage(1);
                    }}
                    className={`uppercase tracking-wider hover:text-gray-900 ${
                      sortBy === 'priority' ? 'text-orange-700' : ''
                    }`}
                    title={sortBy === 'priority' ? 'Sorted by priority — click for newest first' : 'Sort by priority'}
                  >
                    Priority{sortBy === 'priority' ? ' ↑' : ''}
                  </button>
                </th>
                <th className="px-6 py-4 w-[120px]">Status</th>
                <th className="px-6 py-4 w-[110px]">Open</th>
                <th className="px-6 py-4 w-[150px]">Verification</th>
                <th className="px-6 py-4 w-[120px]">Registered</th>
                <th className="px-6 py-4 w-[220px] text-center">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {restaurants.map((restaurant) => (
                <tr
                  key={restaurant.id}
                  className="group hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      data-testid={`restaurant-select-${restaurant.id}`}
                      checked={selectedIds.includes(restaurant.id)}
                      onChange={() => toggleSelectOne(restaurant.id)}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {restaurant.frontImage ? (
                        <img
                          src={restaurant.frontImage}
                          alt={restaurant.name}
                          className="w-12 h-12 rounded-xl object-cover border border-gray-200 shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-gray-100 border border-gray-200 shrink-0" />
                      )}

                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate capitalize flex items-center gap-2">
                          {restaurant.name}
                          {restaurant.isPureVeg && (
                            <span
                              data-testid={`pureveg-badge-${restaurant.id}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 border border-green-300 px-1.5 py-0.5 rounded"
                              title="Pure Veg"
                            >
                              <span className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                              PURE VEG
                            </span>
                          )}
                        </p>

                        <p className="text-gray-700 truncate text-sm capitalize">
                          Owner: {restaurant.ownerName || '-'}
                        </p>

                        <p className="text-xs text-gray-500 line-clamp-2">
                          {restaurant.address || 'No address'}
                          {restaurant.phone ? `, ${restaurant.phone}` : ''}
                          {restaurant.altPhone ? ` / ${restaurant.altPhone}` : ''}
                        </p>

                        {restaurant.zone?.zoneName && (
                          <span
                            data-testid={`restaurant-zone-badge-${restaurant.id}`}
                            className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded"
                            title="Delivery zone"
                          >
                            <span className="w-1.5 h-1.5 bg-orange-600 rounded-full" />
                            ZONE: {restaurant.zone.zoneName}
                          </span>
                        )}

                        {restaurant.paymentReminderPending && (
                          <button
                            type="button"
                            data-testid={`payment-due-badge-${restaurant.id}`}
                            onClick={() => handleClearPaymentReminder(restaurant)}
                            title={`₹${(restaurant.walletBalance || 0).toFixed(2)} pending settlement — click to mark as paid`}
                            className="inline-flex items-center gap-1 mt-1 ml-1 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded hover:bg-red-100"
                          >
                            <span className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                            PAYMENT DUE: ₹{(restaurant.walletBalance || 0).toFixed(0)}
                          </button>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4" data-testid={`restaurant-priority-${restaurant.id}`}>
                    {Number(restaurant.priority) > 0 ? (
                      <span
                        className="inline-flex min-w-[2rem] justify-center rounded-md border border-orange-200 bg-orange-50 px-2 py-0.5 text-sm font-semibold text-orange-700"
                        title="Priority (1 = top)"
                      >
                        {Number(restaurant.priority)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400" title="No priority set">
                        Not set
                      </span>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <Badge
                      className={
                        restaurant.status
                          ? 'bg-green-100 text-green-800 border-0'
                          : 'bg-red-100 text-red-800 border-0'
                      }
                    >
                      {restaurant.status ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>

                  <td className="px-6 py-4">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!restaurant.isOpen}
                      data-testid={`restaurant-isopen-toggle-${restaurant.id}`}
                      onClick={() => toggleRestaurantOpen(restaurant)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-orange-400 ${
                        restaurant.isOpen ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                      title={restaurant.isOpen ? 'Open — click to close' : 'Closed — click to open'}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          restaurant.isOpen ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span
                      className={`ml-2 text-xs font-medium ${
                        restaurant.isOpen ? 'text-green-700' : 'text-gray-500'
                      }`}
                    >
                      {restaurant.isOpen ? 'Open' : 'Closed'}
                    </span>
                  </td>

                  <td className="px-6 py-4">
                    {getStatusBadge(restaurant.verificationStatus)}
                  </td>

                  <td className="px-6 py-4 text-xs text-gray-600">
                    {restaurant.createdAt
                      ? new Date(restaurant.createdAt).toLocaleDateString()
                      : '-'}
                  </td>

                  <td className="px-6 py-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-9 w-full"
                        onClick={() =>
                          (window.location.href = `/restaurants/${restaurant.id}/profile`)
                        }
                      >
                        <Eye size={16} className="mr-1" />
                        Profile
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        className="h-9 w-full"
                        onClick={() => openEditDialog(restaurant)}
                      >
                        <Edit size={16} className="mr-1" />
                        Edit
                      </Button>

                      {/* Legacy rows created before verificationStatus existed
                          have it as undefined/null in Mongo (defaults only
                          apply to new documents, not existing ones), so a
                          strict PENDING-only check would hide Approve/Reject
                          for those restaurants entirely — treat anything
                          that isn't already APPROVED/REJECTED as pending.
                          ⚠️ This used to also hide BOTH buttons entirely once
                          a restaurant was REJECTED, with no way back — a
                          rejected restaurant (e.g. rejected by mistake, or
                          the owner fixed whatever the issue was) could never
                          be approved again from here. Each button now only
                          hides for the state it would be a no-op in, so
                          Approve/Reject stay available and reversible in
                          both directions. */}
                      {restaurant.verificationStatus !== 'APPROVED' ? (
                        <Button
                          size="sm"
                          className="h-9 w-full bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`approve-restaurant-${restaurant.id}`}
                          onClick={() =>
                            updateRestaurantStatus(restaurant.id, {
                              verificationStatus: 'APPROVED'
                            })
                          }
                        >
                          <CheckCircle size={16} className="mr-1" />
                          {restaurant.verificationStatus === 'REJECTED' ? 'Re-approve' : 'Approve'}
                        </Button>
                      ) : (
                        <div />
                      )}

                      {restaurant.verificationStatus !== 'REJECTED' ? (
                        <Button
                          size="sm"
                          variant="destructive"
                          className="h-9 w-full"
                          data-testid={`reject-restaurant-${restaurant.id}`}
                          onClick={() =>
                            updateRestaurantStatus(restaurant.id, {
                              verificationStatus: 'REJECTED'
                            })
                          }
                        >
                          <XCircle size={16} className="mr-1" />
                          Reject
                        </Button>
                      ) : (
                        <div />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {restaurants.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No restaurants found</p>
            </div>
          )}
        </div>
      </div>

      {/* ---------------- PAGINATION ---------------- */}
      {showAll ? (
        <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
          <div data-testid="restaurants-pagination-summary">
            Showing all{' '}
            <span className="font-semibold text-gray-900">{total}</span>{' '}
            restaurant(s)
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
          <div data-testid="restaurants-pagination-summary">
            Showing{' '}
            <span className="font-semibold text-gray-900">
              {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE, total)}
            </span>{' '}
            of <span className="font-semibold text-gray-900">{total}</span>
          </div>
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="restaurants-prev-page"
            >
              Prev
            </Button>
            <span className="px-2">
              Page <span className="font-semibold">{page}</span> / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="restaurants-next-page"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Restaurants;
